/**
 * @fileoverview Role Information Collection Component
 *
 * This component is part of the Heidrick & Struggles Leader Profile onboarding flow.
 * It collects job role information from users including job title, level, dates,
 * function, sub-function, leadership experience, and team management details.
 *
 * The component features:
 * - Dynamic form field display based on configuration
 * - Cascading dropdown selection for job functions and sub-functions
 * - Date validation for start and end dates
 * - "Other" option handling with additional text input
 * - Form validation before submission
 * - Integration with global context for state management
 */

import {
  TextInput,
  Button,
  ButtonVariant,
  TextInputWidth,
  TextInputSize,
  LabelSize,
  Select,
  Label,
  SelectSize,
  DatePicker,
  DatePickerSize,
  IconName,
  ButtonIconAlign,
  EventValue,
} from '@eightfold.ai/octuple';
import React, { useEffect, useState, useContext } from 'react';
import { actionButtonStyle} from '../../commons/commonStyles';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { validateFields, validationOptions } from '../../ValidationSchema';
import { globalContext } from '../../store';
import { checkSubDropDownValues, updateDemographicData, validateDynamicForm, ShowStepper, resolveError, handleDemographicAcknowledge } from '../../commons/commonFunctions';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import ConfirmationModal from '../../commons/ConfirmationModal';

// Extend dayjs with needed plugins
dayjs.extend(weekday);
dayjs.extend(localeData);

const Role = (props: any) => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);

  // ==================== STATE VARIABLES ====================
  // Job function and sub-function related states
  const [jobFunction, setJobFunction] = useState<any>('');
  const [jobSubFuncOptions, setJobSubFuncOptions] = useState<any>([]);
  const [jobSubFunction, setJobSubFunction] = useState<any>('');
  const [describeJobSubFunc, setDescribeJobSubFunc] = useState(false);
  const [jobSubFuncTxt, setjobSubFuncTxt] = useState('');
  const [disableSubFunc, setDisableSubFunc] = useState(true);

  // Basic job information states
  const [jobTitle, setJobTitle] = useState('');
  const [jobLevel, setJobLevel] = useState('');
  const [yearsAtJobLevel, setYearsAtJobLevel] = useState('');
  const [yearsInLeadershipRole, setYearsInLeadershipRole] = useState('');

  // Team management states
  const [directReports, setDirectReports] = useState('');
  const [orgSizeUnderLeader, setOrgSizeUnderLeader] = useState('');

  // Employment dates states
  const [startDate, setStartDate] = useState<any>('');
  const [endDate, setEndDate] = useState<any>('');
  const [UnformattedStartDate, setUnformattedStartDate] = useState<EventValue<dayjs.Dayjs> | null>(null);
  const [UnformattedEndDate, setUnformattedEndDate] = useState<EventValue<dayjs.Dayjs> | null>(null);

  // Form validation and UI states
  const [errors, setErrors] = useState<any>({});
  const [errorMsg, setErrorMsg] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showDemographicModal, setShowDemographicModal] = useState(false);

  // Constants for dropdown options
  const otherTxt = 'Other (please specify)';
  const otherOptionList = [{ text: `${otherTxt}`, value: `${otherTxt}` }];
  /**
   * Initialize form fields with values from props when component loads or props change
   * This effect populates the form with existing data when editing a profile
   */

  useEffect(() => {
    if (props) {
      setJobLevel(props?.jobLevel);
      setJobTitle(props?.jobTitle);
      setYearsAtJobLevel(props?.yearsAtJobLevel);
      setJobFunction(props?.jobFunction);
      
      // Handle "Prefer not to respond" for job function - hide sub-function field
      if (props?.jobFunction === 'Prefer not to respond') {
        setJobSubFunction('Prefer not to respond');
        setJobSubFuncOptions([]);
        setDisableSubFunc(true);
      } else if (props.jobSubFunction && props?.jobSubFunction?.includes('~')) {
        setjobSubFuncTxt(props?.jobSubFunction.replace('OTH~', ''));
        setJobSubFunction(otherTxt);
      } else if (props.jobSubFunction) {
        setJobSubFunction(props.jobSubFunction);
      }

      setYearsInLeadershipRole(props?.yearsInLeadershipRole);
      setDirectReports(props?.directReports);
      setOrgSizeUnderLeader(props?.orgSizeUnderLeadership );
      if (props?.hireDate) {
        setUnformattedStartDate(dayjs(props?.hireDate));
        setStartDate(new Date(props?.hireDate));
      }
      if (props?.terminationDate) {
        setUnformattedEndDate(dayjs(props?.terminationDate));
        setEndDate(new Date(props.terminationDate));
      }
    }
  }, [props]);
  /**
   * Handle sub-function selection based on job function options availability
   * Sets default values for sub-function when applicable or resets when not applicable
   */
  useEffect(() => {
    if (jobSubFuncOptions && jobSubFuncOptions?.[0]?.text === 'Not Applicable') {
      setJobSubFunction('Not Applicable');
    } else if (jobSubFuncOptions && jobSubFuncOptions?.[0]?.text === otherTxt) {
      setJobSubFunction(otherTxt);
    } else if (jobSubFuncOptions && jobSubFuncOptions?.[0]?.text !== 'Not Applicable' && jobFunction && jobSubFunction === 'Not Applicable') {
      setJobSubFunction('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSubFuncOptions]);
  /**
   * Handles job sub-function selection change
   * Shows additional text input field when "Other" is selected
   *
   * @param {Array} value - The selected option value from dropdown
   */
  const changeSubFunction = (value: any) => {
    if (jobSubFunction !== 'Not Applicable') {
      setJobSubFunction(value?.[0]);
    }

    if (value?.[0] === otherTxt) {
      setDescribeJobSubFunc(true);
    } else {
      setDescribeJobSubFunc(false);
    }
  };

  /**
   * Handles job function selection change
   * Updates sub-function dropdown options based on selected job function
   * Manages "Other" and "Not Applicable" cases
   * Hides sub-function field when "Prefer not to respond" is selected
   *
   * @param {string} value - The selected option value from dropdown
   */
  const changeJobFunction = (value: string) => {
    setJobFunction(value?.[0]);
    
    const options = checkSubDropDownValues(value?.[0], 'Job Sub-Function', globalState?.lookUpData);
    if (value?.[0] && options && options?.length > 0) {
      setjobSubFuncTxt('');
      if (value?.[0] === otherTxt) {
        setJobSubFuncOptions(otherOptionList);
        setDisableSubFunc(false);
        if (props.jobSubFunction && props.jobSubFunction?.includes('~')) {
          setjobSubFuncTxt(props.jobSubFunction.replace('OTH~', ''));
        }
        return;
      }
      setJobSubFuncOptions(options);
      setDisableSubFunc(false);
    } else if (value?.[0] === 'Prefer not to respond') {
      setJobSubFuncOptions([]);
      setJobSubFunction('Prefer not to respond');
      setDisableSubFunc(true);
      setjobSubFuncTxt('');
      setDescribeJobSubFunc(false);
    }
    if (!value?.[0]) {
      setDisableSubFunc(true);
    }
  };
  /**
   * Validates end date and job sub-function description
   * Checks if end date is greater than start date
   * Validates "Other" sub-function text input when applicable
   *
   * @returns {Object} - Object containing validation error messages
   */
  const validationForEndDate = () => {
    let endDateMsg;
    let jobSubFuncDesMsg;

    if (Date.parse(endDate) <= Date.parse(startDate)) {
      endDateMsg = 'End date should be greater than start date';
    }

    if (jobSubFunction === otherTxt) {
      jobSubFuncDesMsg = validateFields.validate({ textField: jobSubFuncTxt || '' }, validationOptions)?.error?.details[0]?.message;
    }
    return { endDateMsg, jobSubFuncDesMsg };
  };

  /**
   * Validates all form fields based on which fields are shown
   * Uses dynamic validation based on configured visible fields
   *
   * @returns {boolean} - True if form is invalid, false if valid
   */
  const validateFormData = () => {
    const fieldConfigs = [
      { name: 'showJobLevel', value: jobLevel, type: 'dropDown' },
      { name: 'showJobTitle', value: jobTitle, type: 'textField' },
      { name: 'showHireDate', value: startDate && startDate !== 'Invalid Date' ? 'true' : '', type: 'dropDown' },
      { name: 'showJobFunction', value: jobFunction, type: 'dropDown' },
      { name: 'showJobSubFunction', value: jobSubFunction, type: 'dropDown' },
      { name: 'showYearsAtJobLevel', value: yearsAtJobLevel, type: 'dropDown' },
      { name: 'showYearsInLeadershipRole', value: yearsInLeadershipRole, type: 'dropDown' },
      { name: 'showDirectReports', value: directReports, type: 'dropDown' },
      { name: 'showOrgSizeUnderLeadership', value: orgSizeUnderLeader , type: 'dropDown' },
    ];

    const filteredFieldConfigs = fieldConfigs.filter(field => props?.showFields?.[field.name] === true);

    const customValidations = {
      endDateMsg: () => validationForEndDate().endDateMsg,
      jobSubFuncDesMsg: () => validationForEndDate().jobSubFuncDesMsg,
    };

    const isFormInvalid = validateDynamicForm(filteredFieldConfigs, customValidations, setErrors);
    return isFormInvalid;
  };

  /**
   * Saves role data after validation and updates global context
   * Prepares data payload for API submission and handles "Other" job sub-function case
   * Redirects to next page on successful submission
   */
  const saveRoleData = () => {
    if (validateFormData()) return;
    
    // Determine jobSubFunction value based on jobFunction selection
    let jobSubFunctionValue = jobSubFunction;
    if (jobFunction === 'Prefer not to respond') {
      jobSubFunctionValue = 'Prefer not to respond';
    } else if (jobSubFunction === otherTxt) {
      jobSubFunctionValue = `OTH~${jobSubFuncTxt.trim()}`;
    }
    const trimmedJobTitle = jobTitle?.trim();
    const payload: any = {
      demographicSurveyData: {
        jobTitle: trimmedJobTitle,
        jobLevel: jobLevel,
        yearsAtJobLevel: yearsAtJobLevel,
        yearsInLeadershipRole: yearsInLeadershipRole,
        directReports: directReports,
        jobSubFunction: jobSubFunctionValue,
        jobFunction: jobFunction,
        hireDate: startDate !== 'Invalid Date' ? startDate : '',
        terminationDate: endDate !== 'Invalid Date' ? endDate : '',
        orgSizeUnderLeader: orgSizeUnderLeader,
      },
      isProfileCompleted: ((!props?.isCompanyStepVisible && !props?.isPersonalStepVisible) ? true : false), // Mark profile as completed only if all steps are visible

    };
    if (startDate !== 'Invalid Date') {
      payload.demographicSurveyData['hireDate'] = startDate;
    }

    if (endDate !== 'Invalid Date') {
      payload.demographicSurveyData['terminationDate'] = endDate;
    }

    // Update global context with form data for state persistence
    const updatedDataForContext: any = {
      demographicSurveyData: {
        ...globalState?.leaderData?.demographicSurveyData,
        jobTitle: trimmedJobTitle,
        jobLevel: jobLevel,
        yearsAtJobLevel: yearsAtJobLevel,
        yearsInLeadershipRole: yearsInLeadershipRole,
        directReports: directReports,
        jobSubFunction: jobSubFunctionValue,
        jobFunction: jobFunction,
        hireDate: startDate !== 'Invalid Date' ? startDate : '',
        terminationDate: endDate !== 'Invalid Date' ? endDate : '',
        orgSizeUnderLeader: orgSizeUnderLeader,
      },
      isProfileCompleted: ((!props?.isCompanyStepVisible && !props?.isPersonalStepVisible) ? true : false), // Mark profile as completed only if all steps are visible
    };

    const newContextData = { ...globalState?.leaderData, ...updatedDataForContext };
    const redirectUrl =  ((props?.isCompanyStepVisible) ? '/register-company' :  (props?.isPersonalStepVisible) ? '/register-personal' : '/dashboard');
    updateDemographicData({ payload, leaderProfileId: props.leaderProfileId, redirectUrl, setErrorMsg, newContextData, dispatch, navigate, setShowLoader });
  };

  /**
   * Navigates back to the account page
   * Used for the "Back" button functionality
   */
  const backtoAccountPage = () => {
    navigate('/register-account');
  };

  /**
   * Validates that end date is after start date
   * Used by the DatePicker component to disable invalid dates
   *
   * @param {dayjs.Dayjs} current - The current date being evaluated
   * @returns {boolean} - True if date should be disabled, false otherwise
   */
  const disabledEndDate = (current: any) => {
    return current && current < dayjs(UnformattedStartDate).endOf('day');
  };

  /**
   * Validates that start date is not in the future
   * Used by the DatePicker component to disable invalid dates
   *
   * @param {dayjs.Dayjs} current - The current date being evaluated
   * @returns {boolean} - True if date should be disabled, false otherwise
   */
  const disabledStartDate = (current: any) => {
    return current && current > dayjs().endOf('day');
  };

  /**
   * Effect to show demographic modal on first demographic page
   * Modal appears if this is the first demographic step, demographics are enabled,
   * and the user hasn't acknowledged yet
   */

  const demographicsEnabled = props?.anyDemographicsEnabled === true;
  const isAcknowledged = props?.demographicAcknowledged === true;

  useEffect(() => {
    if (!isAcknowledged && demographicsEnabled) {
      setShowDemographicModal(true);
    }
  },  [demographicsEnabled, isAcknowledged]);

  // Determine if fields should be disabled (when modal hasn't been acknowledged)
  const fieldsDisabled = !isAcknowledged;

  return (
    <div className='accountWrapper'>
      <ShowStepper 
        activeIndex={props?.activeIndex} 
        isRoleStepVisible={props?.isRoleStepVisible}
        isCompanyStepVisible={props?.isCompanyStepVisible}
        isPersonalStepVisible={props?.isPersonalStepVisible}
      />
      <h2 className='onboardingTitle'>About your current or most recent role</h2>

      {props?.jobLevelOptions?.length > 0 && (
        <>
          {/* Job Level field - conditionally rendered based on configuration */}
          {props?.showFields?.showJobLevel && (
            <div className='fieldWrap'>
              <Label htmlFor='jobLevel' classNames='labelText' size={LabelSize.Medium} text='What is your job level?' />
              <Select
                id='jobLevel'
                data-test-id='jobLevel'
                defaultValue={props?.jobLevel}
                onOptionsChange={(e: any) => {
                  setJobLevel(e?.[0]);
                  resolveError('showJobLevel', errors, setErrors);
                }}
                size={SelectSize.Large}
                classNames='selectField'
                options={props?.jobLevelOptions}
                status={errors?.showJobLevelStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showJobLevelMsg && <span className='errorMessage'>{errors?.showJobLevelMsg}</span>}
            </div>
          )}

          {/* Years at current job level field - conditionally rendered based on configuration */}
          {props?.showFields?.showYearsAtJobLevel && (
            <div className='fieldWrap'>
              <Label htmlFor='yearsAtJobLevel' classNames='labelText' size={LabelSize.Medium} text='How many years total have you been at your current job level?' />
              <Select
                id='yearsAtJobLevel'
                data-test-id='yearsAtJobLevel'
                onOptionsChange={(e: any) => {
                  setYearsAtJobLevel(e?.[0]);
                  resolveError('showYearsAtJobLevel', errors, setErrors);
                }}
                size={SelectSize.Large}
                classNames='selectField'
                options={props?.yearsAtJobLevelOptions}
                defaultValue={props?.yearsAtJobLevel}
                status={errors?.showYearsAtJobLevelStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showYearsAtJobLevelMsg && <span className='errorMessage'>{errors?.showYearsAtJobLevelMsg}</span>}
            </div>
          )}

          {/* Years in leadership role field - conditionally rendered based on configuration */}
          {props?.showFields?.showYearsInLeadershipRole && (
            <div className='fieldWrap'>
              <Label htmlFor='yearsInLeadershipRole' classNames='labelText' size={LabelSize.Medium} text='How many years have you been in a leadership role?' />
              <Select
                id='yearsInLeadershipRole'
                data-test-id='yearsInLeadershipRole'
                onOptionsChange={(e: any) => {
                  setYearsInLeadershipRole(e?.[0]);
                  resolveError('showYearsInLeadershipRole', errors, setErrors);
                }}
                size={SelectSize.Large}
                classNames='selectField'
                options={props?.yearsInLeadershipRoleOptions}
                defaultValue={props?.yearsInLeadershipRole}
                status={errors?.showYearsInLeadershipRoleStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showYearsInLeadershipRoleMsg && <span className='errorMessage'>{errors?.showYearsInLeadershipRoleMsg}</span>}
            </div>
          )}

          {/* Direct reports field - conditionally rendered based on configuration */}
          {props?.showFields?.showDirectReports && (
            <div className='fieldWrap'>
              <Label htmlFor='directReports' classNames='labelText' size={LabelSize.Medium} text='How many people directly report to you?' />
              <Select
                id='directReports'
                data-test-id='directReports'
                onOptionsChange={(e: any) => {
                  setDirectReports(e?.[0]);
                  resolveError('showDirectReports', errors, setErrors);
                }}
                size={SelectSize.Large}
                status={errors?.showDirectReportsStatus}
                classNames='selectField'
                options={props?.directReportsOptions}
                defaultValue={props?.directReports}
                disabled={fieldsDisabled}
              />
              {errors?.showDirectReportsMsg && <span className='errorMessage'>{errors?.showDirectReportsMsg}</span>}
            </div>
          )}

          {/* Org size under leader field - conditionally rendered based on configuration */}
          {props?.showFields?.showOrgSizeUnderLeadership && (
            <div className='fieldWrap'>
              <Label htmlFor='orgSizeUnderLeader' classNames='labelText' size={LabelSize.Medium} text='How large is the organization under your leadership?' />
              <Select
                id='orgSizeUnderLeader'
                data-test-id='orgSizeUnderLeader'
                onOptionsChange={(e: any) => {
                  setOrgSizeUnderLeader(e?.[0]);
                  resolveError('showOrgSizeUnderLeadership', errors, setErrors);
                }}
                size={SelectSize.Large}
                status={errors?.showOrgSizeUnderLeadershipStatus}
                classNames='selectField'
                options={props?.orgSizeUnderLeaderOptions}
                defaultValue={props?.orgSizeUnderLeader}
                disabled={fieldsDisabled}
              />
              {errors?.showOrgSizeUnderLeadershipMsg && <span className='errorMessage'>{errors?.showOrgSizeUnderLeadershipMsg}</span>}
            </div>
          )}

          {/* Job Function field - conditionally rendered based on configuration */}
          {props?.showFields?.showJobFunction && (
            <div className='fieldWrap'>
              <Label htmlFor='jobFunction' classNames='labelText' size={LabelSize.Medium} text='What is your job function?' />
              <Select
                id='jobFunction'
                data-test-id='jobFunction'
                onOptionsChange={(value: any) => {
                  changeJobFunction(value);
                  resolveError('showJobFunction', errors, setErrors);
                }}
                size={SelectSize.Large}
                status={errors?.showJobFunctionStatus}
                classNames='selectField'
                options={props?.jobFunctionOptions}
                defaultValue={props?.jobFunction}
                disabled={fieldsDisabled}
              />
              {errors?.showJobFunctionMsg && <span className='errorMessage'>{errors?.showJobFunctionMsg}</span>}
            </div>
          )}

          {/* Job Sub-Function field - conditionally rendered based on configuration and options availability */}
          {props?.showFields?.showJobSubFunction && jobSubFuncOptions?.length > 0 && (
            <div className='fieldWrap'>
              <Label id='subfunction' classNames='labelText' size={LabelSize.Medium} text='What is your job sub-function?' />
              <Select
                area-labelledby='subfunction'
                id='jobSubFunction'
                size={SelectSize.Large}
                onOptionsChange={(value: any) => {
                  changeSubFunction(value);
                  resolveError('showJobSubFunction', errors, setErrors);
                }}
                classNames='selectField'
                options={jobSubFuncOptions !== undefined ? jobSubFuncOptions : []}
                defaultValue={jobSubFunction ?? 'Select'}
                status={errors?.showJobSubFunctionStatus}
                disabled={disableSubFunc || fieldsDisabled}
              />
              {errors?.showJobSubFunctionMsg && <span className='errorMessage'>{errors?.showJobSubFunctionMsg}</span>}
            </div>
          )}

          {/* Additional text field for "Other" job sub-function - only shown when "Other" is selected */}
          {describeJobSubFunc && (
            <div className='fieldWrap'>
              <TextInput
                htmlType='text'
                id='jobSubFuncDescribe'
                clearable
                maxlength={100}
                minlength={0}
                name='jobSubFuncDescribe'
                onChange={e => {
                  setjobSubFuncTxt(e.target.value);
                  resolveError('jobSubFuncDes', errors, setErrors);
                }}
                value={jobSubFuncTxt}
                defaultValue={jobSubFuncTxt}
                inputWidth={TextInputWidth.fill}
                classNames='inputTextField'
                size={TextInputSize.Large}
                status={errors?.jobSubFuncDesMsg ? 'error' : 'validating'}
                disabled={fieldsDisabled}
              />
              {errors?.jobSubFuncDesMsg && <span className='errorMessage'>{errors?.jobSubFuncDesMsg}</span>}
            </div>
          )}

          {/* Job Title field - conditionally rendered based on configuration */}
          {props?.showFields?.showJobTitle && (
            <div className='fieldWrap'>
              <TextInput
                htmlType='text'
                id='jobTitle'
                clearable
                labelProps={{
                  text: 'What is your job title?',
                  size: LabelSize.Medium,
                  classNames: 'labelText'
                }}
                maxlength={100}
                minlength={0}
                name='jobTitle'
                value={jobTitle}
                onChange={(e: any) => {
                  setJobTitle(e.target.value);
                  resolveError('showJobTitle', errors, setErrors);
                }}
                inputWidth={TextInputWidth.fill}
                classNames='inputTextField'
                size={TextInputSize.Large}
                status={errors?.showJobTitleStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showJobTitleMsg && <span className='errorMessage'>{errors?.showJobTitleMsg}</span>}
            </div>
          )}
          
          <div className='twoFieldWrap'>
            {/* Start Date field - conditionally rendered based on configuration */}
            {props?.showFields?.showHireDate && (
              <div className='fieldWrap'>
                <Label htmlFor='startDate' classNames='labelText' size={LabelSize.Medium} text='Start Date' />
                <DatePicker
                  id='startDate'
                  size={DatePickerSize.Large}
                  classNames='datePickerField'
                  disabledDate={disabledStartDate}
                  status={errors?.showHireDateStatus}
                  value={UnformattedStartDate}
                  onChange={(value: EventValue<dayjs.Dayjs> | null) => {
                    setUnformattedStartDate(value);
                    setStartDate(dayjs(value).format('YYYY-MM-DDTHH:mm:ss'));
                    resolveError('showHireDate', errors, setErrors);
                  }}
                  disabled={fieldsDisabled}
                />
                {errors?.showHireDateMsg && <span className='errorMessage'>{errors?.showHireDateMsg}</span>}
              </div>
            )}

            {/* End Date field - conditionally rendered based on configuration */}
            {props?.showFields?.showTerminationDate && (
              <div className='fieldWrap'>
                <Label htmlFor='endDate' classNames='labelText' size={LabelSize.Medium} text='End Date' />
                <DatePicker
                  id='endDate'
                  size={DatePickerSize.Large}
                  disabledDate={disabledEndDate}
                  status={errors?.endDateStatus}
                  value={UnformattedEndDate}
                  onChange={(value: EventValue<dayjs.Dayjs> | null) => {
                    setUnformattedEndDate(value);
                    setEndDate(dayjs(value).format('YYYY-MM-DDTHH:mm:ss'));
                  }}
                  classNames='datePickerField'
                  disabled={fieldsDisabled}
                />
                {errors?.endDateMsg && <span className='errorMessage'>{errors?.endDateMsg}</span>}
              </div>
            )}
          </div>
          
          {/* Navigation buttons for form */}
          <div className='fieldWrap-Btn-multiple'>
            {/* Back button with chevron icon */}
            <Button
              text='Previous'
              variant={ButtonVariant.Secondary}
              classNames='backButton'
              onClick={backtoAccountPage}
              transparent={true}
              iconProps={{ path: IconName.mdiChevronLeft }}
              alignIcon={ButtonIconAlign.Left}
              disabled={fieldsDisabled}
            />

            {/* Continue button with loading state */}
             <Button
              text='Continue'
              variant={ButtonVariant.Secondary}
              classNames='actionButton'
              style={actionButtonStyle}
              onClick={() => {
                saveRoleData();
              }}
              iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : {path:IconName.mdiArrowRight}}
              alignIcon={ButtonIconAlign.Right}
              disabled={showLoader || fieldsDisabled}
            />
          </div>
        </>
      )}
      {errorMsg && <span className='apiErrorMessage'>An error occurred</span>}
      {showDemographicModal && (
        <ConfirmationModal
          header='How we use your responses'
          showModal={showDemographicModal}
          bodyText='The following demographic questions are voluntary and will not be used in the scoring of your assessment or in any employment, promotion, or development decisions. This information is collected solely for research, validation, and fairness monitoring purposes to help ensure the assessment functions consistently and equitably across different groups. Responses will be analyzed in aggregate and handled in accordance with applicable privacy and data protection requirements.'
          showCancelButton={false}
          onContinue={() => handleDemographicAcknowledge(dispatch, setShowDemographicModal)}
          continueBtnTxt={'I understand'}
          onClose={() => setShowDemographicModal(false)}
          isAcknowledge={true}
          maskClosable={false}
        />
      )}
    </div>
  );
};

/**
 * Export the Account component as the default export
 * This allows it to be imported using:
 * import Account from './pages/onboarding/Account';
 */
export default Role;
